/**
 * skipDirectDispatch.ts — BUG-DR-SKIP-DIRECT-DISPATCH (2026-05-21).
 *
 * Account-agnostic client-side helper that turns the task-queue
 * clarifying-card "Ir directo a la Investigación" / "Skip to Deep
 * Research" click into an UNAMBIGUOUS re-dispatch of the user's
 * ORIGINAL deep-research query plus an explicit skip-clarification
 * instruction.
 *
 * --------------------------------------------------------------------
 * Why a bare `skip` was not enough
 * --------------------------------------------------------------------
 * On the Cloud Functions task-queue + in-browser orchestrator path,
 * the bare `skip` message is caught by
 * services/globalTools/orchestrator/midClarifyingFreeTextPreRoute.ts
 * (see findImmediatelyPriorQualificationIndex +
 *  findMostRecentUserQueryBeforeIndex) which re-dispatches
 * deep-research with skipClarification=true and the user's prior
 * query folded in. That works.
 *
 * On Fernando's dedicated-VM tenant (and any future dedicated-VM
 * tenant post-PR #169), the message bypasses the in-browser
 * orchestrator and goes:
 *
 *   frontend
 *     → Cloud Functions prest0nProcessTask
 *     → POST https://<vm>.sslip.io/prest0n/turn
 *     → web-adapter/server.mjs
 *     → openclaw
 *     → OpenRouter
 *     → LLM (Claude/GPT planner)
 *
 * The VM-LLM is a generic planner. It receives:
 *
 *   prior assistant turn: clarifying-card markdown (with three
 *                         clarifying questions and the two buttons)
 *   current user turn:    "skip"
 *
 * Its trained behavior is to read `skip` as the user declining /
 * abandoning the prior request, so it emits a polite Spanish ack
 * "Entendido. ¿En qué te puedo ayudar?" instead of dispatching
 * deep-research. Verified by Malaika's 2026-05-21 13:59-14:03 UTC
 * retest (Slack C0AM8U804KZ/1778281880.499349 message
 * 1779372230.638019, screenshots F0B4WFHTKFZ.png, F0B4WGZ9LGP.png,
 * F0B5DRK3R89.png).
 *
 * --------------------------------------------------------------------
 * The account-agnostic fix
 * --------------------------------------------------------------------
 * Replace the bare `skip` with a structured re-dispatch:
 *
 *   "${originalQuery}
 *
 *   [Skip clarification — run Deep Research now with available
 *   context, no further questions.]"
 *
 * (bilingual — Spanish variant when the original query looks Spanish).
 *
 * This works on BOTH paths:
 *
 *   - Orchestrator path: midClarifyingFreeTextPreRoute still matches
 *     because the qualification card is still the prior assistant
 *     turn; it folds this new message as a scope-refinement note and
 *     re-dispatches deep-research with skipClarification=true.
 *     (Folding the original query with itself is harmless — the
 *     deep-research dispatch is identical to the bare-skip path.)
 *
 *   - VM-LLM path: the message itself is now unambiguous. The LLM
 *     sees the original query repeated with an explicit instruction
 *     to skip clarification, and it dispatches deep-research
 *     directly without further questions.
 *
 * The message is sent HIDDEN via sendProgrammaticMessage(text, {
 * hidden: true }) so it does NOT render as a user-bubble — fixes the
 * cosmetic "bare `skip` user-bubble appears in chat" issue Malaika
 * flagged as problem #3 alongside the routing bug.
 */

import type { ChatMessage } from './types';

// --------------------------------------------------------------------
// findOriginalUserQueryFromHistory
// --------------------------------------------------------------------
// Walks the message history backwards looking for the user query that
// triggered the most-recent deep_research_qualification assistant
// turn. Returns the user query text, or null if either signal is
// missing.
//
// We deliberately do NOT skip over transparent system bubbles here
// (the way midClarifyingFreeTextPreRoute does) because the skip-click
// is a UI affordance attached directly to the qualification card the
// user can see — if the qualification card is no longer the
// most-recent assistant turn from the user's POV, the click handler
// should fall back to bare `skip` rather than guess. The
// transparent-system-bubble compensation already exists in the
// orchestrator pre-route for the in-browser path; this helper is
// scoped to the client-side click handler.
export function findOriginalUserQueryFromHistory(
  history: ReadonlyArray<ChatMessage>,
): string | null {
  if (!history || history.length === 0) return null;

  // (1) Find the most-recent qualification card.
  let qualIdx = -1;
  for (let i = history.length - 1; i >= 0; i--) {
    const msg = history[i];
    if (!msg) continue;
    if (msg.isContextOnly) continue;
    if (msg.sourceTag === 'deep_research_qualification') {
      qualIdx = i;
      break;
    }
  }
  if (qualIdx <= 0) return null;

  // (2) Walk backwards from there to find the most-recent user turn
  //     with non-empty text.
  for (let i = qualIdx - 1; i >= 0; i--) {
    const msg = history[i];
    if (!msg) continue;
    if (msg.isContextOnly) continue;
    if (!msg.isUser) continue;
    const text = (msg.text || '').trim();
    if (text) return text;
  }
  return null;
}

// --------------------------------------------------------------------
// pickOriginalQueryForSkip
// --------------------------------------------------------------------
// BUG-DR-SKIP-ORIGINAL-QUERY (2026-06-05) — pure priority resolver for the
// skip-click handler. The handler must recover the original DR query even
// when the user FRAGMENTED the task across threads (the qualification card +
// original prompt then live in a different thread's in-memory history, so the
// history-walk yields null and the old code fell back to a bare `skip` that
// the planner answers on the lightweight lane — "no extensive compute", zero
// metering, zero wallet debit).
//
// Priority:
//   (1) `stampedQuery` — the skip button's own `data-original-query` attribute
//       (stamped at render time from the backend stage payload; self-contained,
//       thread-fragmentation proof).
//   (2) `historyQuery` — the active-thread history walk
//       (findOriginalUserQueryFromHistory result).
//   (3) null — caller falls back to bare `skip`.
// Whitespace-only values are treated as absent.
export function pickOriginalQueryForSkip(
  stampedQuery: string | null | undefined,
  historyQuery: string | null | undefined,
): string | null {
  const stamped = (stampedQuery || '').trim();
  if (stamped) return stamped;
  const hist = (historyQuery || '').trim();
  if (hist) return hist;
  return null;
}

// --------------------------------------------------------------------
// detectSpanishFromQuery
// --------------------------------------------------------------------
// Quick-and-dirty heuristic mirroring the private `looksSpanish` in
// services/globalTools/orchestrator/refinementPreRoute.ts so both code
// paths agree on what counts as Spanish. Used here only to pick the
// language of the [skip-clarification] instruction we append to the
// re-dispatched query.
export function detectSpanishFromQuery(text: string): boolean {
  if (!text) return false;
  // Spanish diacritics and inverted punctuation.
  if (/[áéíóúñÁÉÍÓÚÑ¿¡]/.test(text)) return true;
  // High-signal Spanish keywords common in deep-research prompts.
  return /\b(?:investiga|investigación|investigacion|jurisprudencia|corte|tribunal|sentencia|dame|tu|tú|profund|amplía|amplia|memo|respuesta)\b/i.test(
    text,
  );
}

// --------------------------------------------------------------------
// buildSkipDispatchMessage
// --------------------------------------------------------------------
// Compose the structured re-dispatch text — original query + an
// unambiguous bilingual instruction telling the planner to skip
// clarification and run deep-research immediately. Pure function;
// trivially testable.
export function buildSkipDispatchMessage(
  originalQuery: string,
  isSpanish: boolean,
): string {
  const instruction = isSpanish
    ? '[Salta las preguntas aclaratorias y ejecuta la investigación profunda inmediatamente con el contexto disponible. No hagas más preguntas.]'
    : '[Skip clarification — run Deep Research now with available context, no further questions.]';
  return `${originalQuery}\n\n${instruction}`;
}
